/**
 * Announcement Countdown Bar Custom Element
 * Handles countdown timer updates and discount code copying
 */

class AnnouncementCountdown extends HTMLElement {
  constructor() {
    super();
    this.updateInterval = null;
    this.targetTimestamp = null;
    this.showDays = true;
    this.showHours = true;
    this.showMinutes = true;
  }

  connectedCallback() {
    // Get configuration from data attributes
    this.targetTimestamp = parseInt(this.dataset.targetTimestamp, 10);
    this.showDays = this.dataset.showDays === 'true';
    this.showHours = this.dataset.showHours === 'true';
    this.showMinutes = this.dataset.showMinutes === 'true';

    // Set up discount code button if present
    const button = this.querySelector('.countdown-bar__button');
    if (button) {
      button.addEventListener('click', this.handleCopyCode.bind(this));
    }

    // Start countdown updates
    this.startCountdown();
  }

  disconnectedCallback() {
    // Clean up interval when element is removed
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }
  }

  startCountdown() {
    // Update immediately
    this.updateCountdown();

    // Then update every 60 seconds (1 minute)
    this.updateInterval = setInterval(() => {
      this.updateCountdown();
    }, 60000);
  }

  updateCountdown() {
    const now = Math.floor(Date.now() / 1000);
    const timeLeft = this.targetTimestamp - now;

    // If countdown is finished, hide the bar
    if (timeLeft <= 0) {
      this.hideCountdown();
      return;
    }

    // Calculate time units
    const days = Math.floor(timeLeft / 86400);
    const hours = Math.floor((timeLeft % 86400) / 3600);
    const minutes = Math.floor((timeLeft % 3600) / 60);

    // Update display
    if (this.showDays) {
      const daysElement = this.querySelector('[data-unit="days"]');
      if (daysElement) {
        daysElement.textContent = this.padNumber(days);
      }
    }

    if (this.showHours) {
      const hoursElement = this.querySelector('[data-unit="hours"]');
      if (hoursElement) {
        hoursElement.textContent = this.padNumber(hours);
      }
    }

    if (this.showMinutes) {
      const minutesElement = this.querySelector('[data-unit="minutes"]');
      if (minutesElement) {
        minutesElement.textContent = this.padNumber(minutes);
      }
    }
  }

  padNumber(num) {
    // Pad single-digit numbers with leading zero
    return num < 10 ? `0${num}` : `${num}`;
  }

  hideCountdown() {
    // Clear the interval
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }

    // Fade out and remove the element
    this.style.transition = 'opacity 0.5s ease-out';
    this.style.opacity = '0';

    setTimeout(() => {
      this.style.display = 'none';
      this.remove();
    }, 500);
  }

  async handleCopyCode(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const code = button.dataset.discountCode;
    const buttonText = button.dataset.buttonText;
    const successText = button.dataset.successText;

    if (!code) return;

    // Try to copy to clipboard
    const copied = await this.copyToClipboard(code);

    if (copied) {
      this.showSuccessState(button, buttonText, successText);
    }
  }

  async copyToClipboard(text) {
    // Try modern Clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (err) {
        console.warn('Clipboard API failed, trying fallback:', err);
      }
    }

    // Fallback for older browsers
    return this.copyToClipboardFallback(text);
  }

  copyToClipboardFallback(text) {
    // Create temporary textarea element
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.width = '1px';
    textarea.style.height = '1px';
    textarea.style.padding = '0';
    textarea.style.border = 'none';
    textarea.style.outline = 'none';
    textarea.style.boxShadow = 'none';
    textarea.style.background = 'transparent';
    textarea.setAttribute('readonly', '');

    document.body.appendChild(textarea);
    textarea.select();

    let success = false;
    try {
      success = document.execCommand('copy');
    } catch (err) {
      console.error('Fallback copy failed:', err);
    }

    document.body.removeChild(textarea);
    return success;
  }

  showSuccessState(button, originalText, successText) {
    // Add success class for styling
    button.classList.add('countdown-bar__button--success');
    
    // Update button text
    const textElement = button.querySelector('.countdown-bar__button-text');
    if (textElement) {
      textElement.textContent = successText;
    }

    // Disable button temporarily
    button.disabled = true;

    // Revert after 3 seconds
    setTimeout(() => {
      button.classList.remove('countdown-bar__button--success');
      if (textElement) {
        textElement.textContent = originalText;
      }
      button.disabled = false;
    }, 3000);
  }
}

// Define the custom element
if (!customElements.get('announcement-countdown')) {
  customElements.define('announcement-countdown', AnnouncementCountdown);
}

export default AnnouncementCountdown;

